import { addDays, addMonths } from "date-fns";
import { compact } from "lodash";
import { type Paywall } from "@/react/types";
import type { CouponApplications } from "@/react/types/Paywall";
import type { PaywallPrice } from "@/react/types/PaywallPrice";
import type { ShowEndpointQueryParams } from "@circle-react/api/paywallCheckoutApi.types";
import {
  isFixed,
  isForeverCoupon,
  isPercent,
} from "@circle-react/helpers/paywallCouponHelpers";
import {
  isRecurringPrice,
  isTrialThenUpfrontThenValue,
  isTrialThenValue,
  isUpfrontThenTrialThenValue,
  isUpfrontThenValue,
} from "@circle-react/helpers/paywallPriceHelpers";
import { amountInUnits } from "@circle-react/helpers/paywalls/currencies";
import {
  INTERVAL_TYPE_TO_PERIOD,
  getDiscountAmount,
  getRecurrentPrice,
  isCouponApplicable,
} from "../../helpers/mockedPreviewers";
import type { CheckoutProps } from "../../types/CheckoutProps";
import type { GeneratorParameters } from "../usePreviewMutation/fetchMockedData";

interface CouponApplicationsState {
  applications: CouponApplications["applications"];
  endDate: Date;
  pointer: Date;
  startDate: Date;
}

type IntervalStrategy = [typeof addDays, number];

const applyCoupon = (
  { applications, startDate, endDate, pointer }: CouponApplicationsState,
  discountAmount: number,
  addIntervalStrategy: IntervalStrategy,
  couponDurationType?: "forever" | "once" | "repeating",
): CouponApplicationsState => {
  const isOnceCouponApplicable =
    couponDurationType === "once" &&
    !applications.find(discount => discount.discount_amount > 0);
  const isRecurrentCouponApplicable =
    (couponDurationType === "forever" || couponDurationType === "repeating") &&
    (pointer >= startDate || pointer <= endDate);

  const newApplication =
    isRecurrentCouponApplicable || isOnceCouponApplicable
      ? [
          {
            discount_amount: discountAmount,
            applied_at: pointer.toISOString(),
          },
        ]
      : [];

  const [addInterval, interval] = addIntervalStrategy;

  return {
    startDate,
    endDate,
    pointer: addInterval(pointer, interval),
    applications: [...applications, ...newApplication],
  };
};

const getCouponDuration = (
  props: GeneratorParameters<ShowEndpointQueryParams>,
) => {
  const paywallCoupon = props?.checkoutPreviewContext?.coupon_applied;

  if (
    paywallCoupon?.duration === "forever" ||
    paywallCoupon?.duration === "once"
  ) {
    return 720;
  }

  return paywallCoupon?.duration_in_months || 0;
};

const getApplications = (
  props: GeneratorParameters<ShowEndpointQueryParams>,
  price: PaywallPrice,
) => {
  const priceScenario = price.scenario;

  const startDate = new Date();
  const endDate = addMonths(startDate, getCouponDuration(props));
  const pointerDate = startDate;
  const paywallCoupon = props?.checkoutPreviewContext?.coupon_applied;

  const trialIntervalStrategy: IntervalStrategy = [
    addDays,
    props.paywall.trial_days,
  ];
  let couponApplicationsState: CouponApplicationsState = {
    applications: [],
    endDate,
    pointer: pointerDate,
    startDate,
  };

  if (isTrialThenUpfrontThenValue(priceScenario)) {
    // Trial
    couponApplicationsState = applyCoupon(
      couponApplicationsState,
      0,
      trialIntervalStrategy,
      paywallCoupon?.duration,
    );

    // Upfront
    couponApplicationsState = applyCoupon(
      couponApplicationsState,
      -1 * getDiscountAmount(props, price.upfront_payment_amount || 0, price),
      INTERVAL_TYPE_TO_PERIOD[price.interval_type],
      paywallCoupon?.duration,
    );
  }

  if (isTrialThenValue(priceScenario)) {
    // Trial
    couponApplicationsState = applyCoupon(
      couponApplicationsState,
      0,
      trialIntervalStrategy,
      paywallCoupon?.duration,
    );
  }

  if (isUpfrontThenTrialThenValue(priceScenario)) {
    // Upfront
    couponApplicationsState = applyCoupon(
      couponApplicationsState,
      -1 * getDiscountAmount(props, price?.upfront_payment_amount || 0, price),
      INTERVAL_TYPE_TO_PERIOD[price.interval_type],
      paywallCoupon?.duration,
    );

    // Trial
    couponApplicationsState = applyCoupon(
      couponApplicationsState,
      0,
      trialIntervalStrategy,
      paywallCoupon?.duration,
    );
  }

  if (isUpfrontThenValue(priceScenario)) {
    // Upfront
    couponApplicationsState = applyCoupon(
      couponApplicationsState,
      -1 * getDiscountAmount(props, price?.upfront_payment_amount || 0, price),
      INTERVAL_TYPE_TO_PERIOD[price.interval_type],
      paywallCoupon?.duration,
    );
  }

  // Value
  couponApplicationsState = applyCoupon(
    couponApplicationsState,
    -1 * getDiscountAmount(props, getRecurrentPrice(price), price),
    INTERVAL_TYPE_TO_PERIOD[price.interval_type],
    paywallCoupon?.duration,
  );

  return compact(couponApplicationsState.applications);
};

const generateCouponApplications = (
  props: GeneratorParameters<ShowEndpointQueryParams>,
  price: PaywallPrice,
): CouponApplications => {
  const paywallCoupon = props?.checkoutPreviewContext?.coupon_applied;

  let applications: CouponApplications["applications"] = [];
  if (isCouponApplicable(props, price)) {
    applications = getApplications(props, price);
  }

  const isCouponForeverApplicable =
    isForeverCoupon(paywallCoupon?.duration) && applications.length > 0;
  const counter = isCouponForeverApplicable ? "Infinity" : applications.length;
  const hasMore = isCouponForeverApplicable ? true : false;

  const couponAmount = isFixed(paywallCoupon?.duration)
    ? amountInUnits(props.paywall.currency, paywallCoupon?.amount || 0)
    : Number(paywallCoupon?.amount);

  const isFreeForever =
    !isForeverCoupon(paywallCoupon?.duration) && isRecurringPrice(price)
      ? false
      : (isPercent(paywallCoupon?.type) && couponAmount === 100) ||
        (isFixed(paywallCoupon?.type) && couponAmount >= price.amount);

  return {
    has_more: hasMore,
    free_forever: isFreeForever,
    application_count: counter,
    applications,
  };
};

const generate = (props: GeneratorParameters<ShowEndpointQueryParams>) => {
  // eslint-disable-next-line sonarjs/different-types-comparison -- Disabled for plugin version update. Fix when possible
  if (props === undefined) {
    return undefined;
  }

  const paywallCoupon = props?.checkoutPreviewContext?.coupon_applied;

  return {
    paywall_coupon: paywallCoupon,
    paywall: {
      ...props.paywall,
      prices: props.paywall.prices.map((price: PaywallPrice) => ({
        ...price,
        coupon_applications: generateCouponApplications(props, price),
      })),
    },
  };
};

export const fetchMockedData =
  (
    paywall: Paywall,
    checkoutPreviewContext: CheckoutProps["checkoutPreviewContext"],
  ) =>
  async (params: ShowEndpointQueryParams) => {
    if (!params || !paywall) {
      const payload: any = undefined;
      return payload;
    }

    await new Promise(resolve => setTimeout(resolve, 500));

    return generate({ params, paywall, checkoutPreviewContext });
  };
